import { useReducer, useCallback, useMemo } from 'react';
import type { BlockNode, DocumentAST, ViewportDevice, StyleTargetState } from '../types';
import {
  insertNodeAtIndex,
  moveNodeDirection,
  moveNodeToParent,
  duplicateNodeInTree,
  wrapNodeInContainer,
  removeNodeFromTree,
  findNodeById,
  ensureSiteChromeNodes,
} from '../utils';

const MAX_HISTORY_STEPS = 100;
const COALESCE_WINDOW_MS = 600;

interface HistoryState {
  past: DocumentAST[];
  present: DocumentAST;
  future: DocumentAST[];
  lastMutationKey?: string;
  lastMutationTime?: number;
}

type HistoryAction =
  | { type: 'MUTATE'; transform: (prev: DocumentAST) => DocumentAST; coalesceKey?: string }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESET'; newDoc: DocumentAST };

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'MUTATE': {
      const now = Date.now();
      const nextPresent = action.transform(state.present);
      if (nextPresent === state.present) {
        return state;
      }

      const isCoalesced =
        action.coalesceKey &&
        state.lastMutationKey === action.coalesceKey &&
        state.lastMutationTime &&
        now - state.lastMutationTime < COALESCE_WINDOW_MS;

      if (isCoalesced) {
        return {
          ...state,
          present: nextPresent,
          lastMutationTime: now,
        };
      }

      return {
        past: [...state.past.slice(-(MAX_HISTORY_STEPS - 1)), state.present],
        present: nextPresent,
        future: [],
        lastMutationKey: action.coalesceKey,
        lastMutationTime: now,
      };
    }

    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, -1);
      return {
        past: newPast,
        present: previous,
        future: [state.present, ...state.future],
        lastMutationKey: undefined,
        lastMutationTime: undefined,
      };
    }

    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      const newFuture = state.future.slice(1);
      return {
        past: [...state.past, state.present],
        present: next,
        future: newFuture,
        lastMutationKey: undefined,
        lastMutationTime: undefined,
      };
    }

    case 'RESET': {
      return {
        past: [],
        present: action.newDoc,
        future: [],
        lastMutationKey: undefined,
        lastMutationTime: undefined,
      };
    }

    default:
      return state;
  }
}

function updateNodeRecursively(
  root: BlockNode,
  targetId: string,
  transform: (node: BlockNode) => BlockNode
): BlockNode {
  if (root.id === targetId) {
    return transform(root);
  }
  if (!root.children) {
    return root;
  }
  return {
    ...root,
    children: root.children.map((child) => updateNodeRecursively(child, targetId, transform)),
  };
}

export interface UseCanvasHistoryReturn {
  doc: DocumentAST;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  insertNode: (parentId: string, node: BlockNode) => void;
  insertNodeAt: (parentId: string, node: BlockNode, index?: number) => void;
  moveNode: (nodeId: string, direction: 'up' | 'down') => void;
  moveNodeTo: (sourceId: string, destParentId: string, destIndex: number) => void;
  duplicateNode: (nodeId: string) => string | null;
  wrapInContainer: (nodeId: string) => string;
  updateNodeProps: (nodeId: string, props: Record<string, unknown>) => void;
  updateNodeStyles: (nodeId: string, breakpoint: 'base' | 'md', styles: Record<string, string>) => void;
  updateNodeStylesTarget: (
    nodeId: string,
    stylePatch: Record<string, string | undefined>,
    target?: { breakpoint?: ViewportDevice; state?: StyleTargetState }
  ) => void;
  updateNodeMeta: (
    nodeId: string,
    meta: Partial<Pick<BlockNode, 'customName' | 'htmlId' | 'classes' | 'hidden' | 'locked'>>
  ) => void;
  reparentBlock: (nodeId: string, targetParentId: string | null, index?: number) => void;
  removeNode: (nodeId: string) => void;
  resetDoc: (newDoc: DocumentAST) => void;
}

// useCanvasHistory provides a bounded 100-step structural snapshot undo/redo history with mutation coalescing.
export function useCanvasHistory(initialDoc: DocumentAST): UseCanvasHistoryReturn {
  const sanitizedInitialDoc = useMemo(
    () => ({
      ...initialDoc,
      root: ensureSiteChromeNodes(initialDoc.root),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [state, dispatch] = useReducer(historyReducer, {
    past: [],
    present: sanitizedInitialDoc,
    future: [],
  });

  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
  const resetDoc = useCallback(
    (newDoc: DocumentAST) =>
      dispatch({
        type: 'RESET',
        newDoc: { ...newDoc, root: ensureSiteChromeNodes(newDoc.root) },
      }),
    []
  );

  const insertNode = useCallback((parentId: string, node: BlockNode) => {
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const targetParentId = parentId || prev.root.id;
        const nextRoot = ensureSiteChromeNodes(insertNodeAtIndex(prev.root, targetParentId, node));
        return { ...prev, root: nextRoot };
      },
    });
  }, []);

  const insertNodeAt = useCallback((parentId: string, node: BlockNode, index?: number) => {
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const targetParentId = parentId || prev.root.id;
        const nextRoot = ensureSiteChromeNodes(insertNodeAtIndex(prev.root, targetParentId, node, index));
        return { ...prev, root: nextRoot };
      },
    });
  }, []);

  const moveNode = useCallback((nodeId: string, direction: 'up' | 'down') => {
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const targetNode = findNodeById(prev.root, nodeId);
        if (targetNode?.type === 'SiteNavbar' || targetNode?.type === 'SiteFooter') {
          return prev; // Never move pinned navbar or footer
        }
        const nextRoot = ensureSiteChromeNodes(moveNodeDirection(prev.root, nodeId, direction));
        return { ...prev, root: nextRoot };
      },
    });
  }, []);

  const moveNodeTo = useCallback((sourceId: string, destParentId: string, destIndex: number) => {
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const targetNode = findNodeById(prev.root, sourceId);
        if (targetNode?.type === 'SiteNavbar' || targetNode?.type === 'SiteFooter') {
          return prev; // Never reparent pinned navbar or footer
        }
        const nextRoot = ensureSiteChromeNodes(moveNodeToParent(prev.root, sourceId, destParentId, destIndex));
        return { ...prev, root: nextRoot };
      },
    });
  }, []);

  const duplicateNode = useCallback((nodeId: string): string | null => {
    let duplicatedId: string | null = null;
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const result = duplicateNodeInTree(prev.root, nodeId);
        duplicatedId = result.duplicatedId;
        return { ...prev, root: result.newRoot };
      },
    });
    return duplicatedId;
  }, []);

  const wrapInContainer = useCallback((nodeId: string): string => {
    let containerId = '';
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const result = wrapNodeInContainer(prev.root, nodeId);
        containerId = result.containerId;
        return { ...prev, root: result.newRoot };
      },
    });
    return containerId;
  }, []);

  const updateNodeProps = useCallback((nodeId: string, props: Record<string, unknown>) => {
    dispatch({
      type: 'MUTATE',
      coalesceKey: `props:${nodeId}`,
      transform: (prev) => {
        const nextRoot = updateNodeRecursively(prev.root, nodeId, (node) => ({
          ...node,
          props: { ...node.props, ...props },
        }));
        return { ...prev, root: nextRoot };
      },
    });
  }, []);

  const updateNodeStyles = useCallback(
    (nodeId: string, bp: 'base' | 'md', styles: Record<string, string>) => {
      dispatch({
        type: 'MUTATE',
        coalesceKey: `styles:${nodeId}:${bp}`,
        transform: (prev) => {
          const nextRoot = updateNodeRecursively(prev.root, nodeId, (node) => ({
            ...node,
            styles: {
              ...node.styles,
              [bp]: { ...node.styles[bp], ...styles },
            },
          }));
          return { ...prev, root: nextRoot };
        },
      });
    },
    []
  );

  const updateNodeStylesTarget = useCallback(
    (
      nodeId: string,
      stylePatch: Record<string, string | undefined>,
      target?: { breakpoint?: ViewportDevice; state?: StyleTargetState }
    ) => {
      const bp = target?.breakpoint || 'desktop';
      const st = target?.state || 'default';
      const sliceKey =
        st === 'hover'
          ? 'hover'
          : st === 'active'
          ? 'active'
          : bp === 'tablet'
          ? 'tablet'
          : bp === 'mobile'
          ? 'mobile'
          : 'base';

      dispatch({
        type: 'MUTATE',
        coalesceKey: `styles:${nodeId}:${sliceKey}`,
        transform: (prev) => {
          const nextRoot = updateNodeRecursively(prev.root, nodeId, (node) => {
            const currentSlice = { ...((node.styles as any)?.[sliceKey] || {}) };
            for (const [key, val] of Object.entries(stylePatch)) {
              if (val === undefined || val === '') {
                delete currentSlice[key];
              } else {
                currentSlice[key] = val;
              }
            }
            return {
              ...node,
              styles: {
                ...node.styles,
                [sliceKey]: currentSlice,
              },
            };
          });
          return { ...prev, root: nextRoot };
        },
      });
    },
    []
  );

  const updateNodeMeta = useCallback(
    (
      nodeId: string,
      meta: Partial<Pick<BlockNode, 'customName' | 'htmlId' | 'classes' | 'hidden' | 'locked'>>
    ) => {
      dispatch({
        type: 'MUTATE',
        coalesceKey: `meta:${nodeId}`,
        transform: (prev) => {
          const nextRoot = updateNodeRecursively(prev.root, nodeId, (node) => ({
            ...node,
            ...meta,
          }));
          return { ...prev, root: nextRoot };
        },
      });
    },
    []
  );

  const reparentBlock = useCallback(
    (nodeId: string, targetParentId: string | null, index?: number) => {
      dispatch({
        type: 'MUTATE',
        transform: (prev) => {
          const targetParent = targetParentId || prev.root.id;
          const nextRoot = moveNodeToParent(prev.root, nodeId, targetParent, index ?? 0);
          return { ...prev, root: nextRoot };
        },
      });
    },
    []
  );

  const removeNode = useCallback((nodeId: string) => {
    dispatch({
      type: 'MUTATE',
      transform: (prev) => {
        const targetNode = findNodeById(prev.root, nodeId);
        if (targetNode?.type === 'SiteNavbar' || targetNode?.type === 'SiteFooter') {
          return prev; // Permanent site chrome cannot be deleted (Requirement 5)
        }
        return {
          ...prev,
          root: ensureSiteChromeNodes(removeNodeFromTree(prev.root, nodeId)),
        };
      },
    });
  }, []);

  return useMemo(
    () => ({
      doc: state.present,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      undo,
      redo,
      insertNode,
      insertNodeAt,
      moveNode,
      moveNodeTo,
      duplicateNode,
      wrapInContainer,
      updateNodeProps,
      updateNodeStyles,
      updateNodeStylesTarget,
      updateNodeMeta,
      reparentBlock,
      removeNode,
      resetDoc,
    }),
    [
      state.present,
      state.past.length,
      state.future.length,
      undo,
      redo,
      insertNode,
      insertNodeAt,
      moveNode,
      moveNodeTo,
      duplicateNode,
      wrapInContainer,
      updateNodeProps,
      updateNodeStyles,
      updateNodeStylesTarget,
      updateNodeMeta,
      reparentBlock,
      removeNode,
      resetDoc,
    ]
  );
}
